import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { Project } from '@/types/database';
import { useProjects } from '@/hooks/useProjects';
import { useGlobalProjectSync } from '@/hooks/useRealtimeSync';

interface ActiveProjectContextValue {
  activeProjectId: string | null;
  activeProject: Project | null;
  setActiveProjectId: (id: string | null) => void;
  clearActiveProject: () => void;
  // Computed helpers
  isOutOfSync: boolean;
  // Focus lock/unlock toggle
  isFocusUnlocked: boolean;
  toggleFocusLock: () => void;
}

const ActiveProjectContext = createContext<ActiveProjectContextValue | undefined>(undefined);

const STORAGE_KEY = 'verithos_active_project';
const FOCUS_UNLOCK_KEY = 'verithos_focus_unlocked';

interface ActiveProjectProviderProps {
  children: ReactNode;
}

export function ActiveProjectProvider({ children }: ActiveProjectProviderProps) {
  const { projects, isLoading } = useProjects();
  
  // Wire global project sync for real-time updates across all projects
  useGlobalProjectSync();
  const [activeProjectId, setActiveProjectIdState] = useState<string | null>(() => {
    // Initialize from localStorage
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY);
    }
    return null;
  });
  
  // Focus unlock state - allows temporary access to Focus page for active project
  const [isFocusUnlocked, setIsFocusUnlocked] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(FOCUS_UNLOCK_KEY) === 'true';
    }
    return false;
  });

  // Validate stored project ID still exists when projects load
  // Only clear if projects have loaded AND the ID was set before this session
  // (i.e., from localStorage on mount, not from a recent setActiveProjectId call)
  const [justSetId, setJustSetId] = useState(false);
  
  useEffect(() => {
    if (!isLoading && activeProjectId && projects.length > 0 && !justSetId) {
      const projectExists = projects.some(p => p.id === activeProjectId);
      if (!projectExists) {
        // Clear invalid stored project
        localStorage.removeItem(STORAGE_KEY);
        setActiveProjectIdState(null);
      }
    }
    // Reset the flag once projects have refreshed and include the new project
    if (justSetId && projects.some(p => p.id === activeProjectId)) {
      setJustSetId(false);
    }
  }, [projects, isLoading, activeProjectId, justSetId]);

  // Get the full project object for the active ID
  const activeProject = activeProjectId 
    ? projects.find(p => p.id === activeProjectId) ?? null 
    : null;

  // Compute isOutOfSync: focus_updated_at > last_analysis_at
  const isOutOfSync = useMemo(() => {
    if (!activeProject) return false;
    const focusUpdatedAt = activeProject.focus_updated_at 
      ? new Date(activeProject.focus_updated_at) 
      : null;
    const lastAnalysisAt = activeProject.last_analysis_at 
      ? new Date(activeProject.last_analysis_at) 
      : null;
    
    // Out of sync if focus was updated after last analysis
    if (focusUpdatedAt && lastAnalysisAt) {
      return focusUpdatedAt > lastAnalysisAt;
    }
    // If we have focus but no analysis, we're technically "in sync" (nothing to be out of sync with)
    return false;
  }, [activeProject]);

  const setActiveProjectId = useCallback((id: string | null) => {
    setActiveProjectIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
      setJustSetId(true); // Prevent validation from clearing a freshly-set ID
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const clearActiveProject = useCallback(() => {
    setActiveProjectIdState(null);
    localStorage.removeItem(STORAGE_KEY);
    // Reset focus unlock when clearing project
    setIsFocusUnlocked(false);
    localStorage.removeItem(FOCUS_UNLOCK_KEY);
  }, []);
  
  const toggleFocusLock = useCallback(() => {
    setIsFocusUnlocked(prev => {
      const newValue = !prev;
      if (newValue) {
        localStorage.setItem(FOCUS_UNLOCK_KEY, 'true');
      } else {
        localStorage.removeItem(FOCUS_UNLOCK_KEY);
      }
      return newValue;
    });
  }, []);
  
  // Reset focus unlock when project changes
  useEffect(() => {
    setIsFocusUnlocked(false);
    localStorage.removeItem(FOCUS_UNLOCK_KEY);
  }, [activeProjectId]);

  return (
    <ActiveProjectContext.Provider 
      value={{ 
        activeProjectId, 
        activeProject, 
        setActiveProjectId, 
        clearActiveProject,
        isOutOfSync,
        isFocusUnlocked,
        toggleFocusLock,
      }}
    >
      {children}
    </ActiveProjectContext.Provider>
  );
}

export function useActiveProject() {
  const context = useContext(ActiveProjectContext);
  if (context === undefined) {
    throw new Error('useActiveProject must be used within an ActiveProjectProvider');
  }
  return context;
}
